import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { X, TrendingUp, Clock, Target, Flame, Calendar } from 'lucide-react';

const SessionStats = ({ stats, history, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };
  
  // Get recent sessions (last 10)
  const recentSessions = history.slice(0, 10);
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 to-slate-800 border-white/20">
        <CardHeader className="relative">
          <Button
            onClick={onClose}
            aria-label="Close stats"
            data-testid="stats-close-btn"
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 text-white hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </Button>
          <CardTitle className="text-white text-2xl flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-400" />
            Your Progress & Stats
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total Sessions */}
            <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 rounded-lg p-4 border border-purple-500/30">
              <div className="flex items-center gap-3 mb-2">
                <Target className="w-8 h-8 text-purple-400" />
                <div>
                  <div className="text-3xl font-bold text-white">{stats.totalSessions}</div>
                  <div className="text-xs text-white/60">Total Sessions</div>
                </div>
              </div>
            </div>
            
            {/* Total Time */}
            <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-lg p-4 border border-blue-500/30">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-8 h-8 text-blue-400" />
                <div>
                  <div className="text-3xl font-bold text-white">{stats.totalMinutes}</div>
                  <div className="text-xs text-white/60">Minutes Focused</div>
                </div>
              </div>
            </div>
            
            {/* Current Streak */}
            <div className="bg-gradient-to-br from-orange-600/20 to-orange-800/20 rounded-lg p-4 border border-orange-500/30">
              <div className="flex items-center gap-3 mb-2">
                <Flame className="w-8 h-8 text-orange-400" />
                <div>
                  <div className="text-3xl font-bold text-white">{stats.currentStreak}</div>
                  <div className="text-xs text-white/60">Day Streak 🔥</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Most Used Preset */}
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="text-white/60 text-sm mb-2">Favorite Preset</div>
              <div className="text-xl font-semibold text-white">
                {stats.mostUsedPreset || 'No presets used yet'}
              </div>
            </div>
            
            {/* Longest Streak */}
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="text-white/60 text-sm mb-2">Longest Streak</div>
              <div className="text-xl font-semibold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-400" />
                {stats.longestStreak} days
              </div>
            </div>
          </div>
          
          {/* Milestones */}
          {stats.totalMinutes > 0 && (
            <div className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 rounded-lg p-4 border border-white/10">
              <div className="text-white font-semibold mb-3 flex items-center gap-2">
                🏆 Achievements
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {stats.totalSessions >= 1 && (
                  <div className="text-center">
                    <div className="text-2xl mb-1">🎯</div>
                    <div className="text-xs text-white/70">First Session</div>
                  </div>
                )}
                {stats.totalSessions >= 5 && (
                  <div className="text-center">
                    <div className="text-2xl mb-1">⭐</div>
                    <div className="text-xs text-white/70">5 Sessions</div>
                  </div>
                )}
                {stats.totalMinutes >= 60 && (
                  <div className="text-center">
                    <div className="text-2xl mb-1">⏰</div>
                    <div className="text-xs text-white/70">1 Hour Total</div>
                  </div>
                )}
                {stats.currentStreak >= 3 && (
                  <div className="text-center">
                    <div className="text-2xl mb-1">🔥</div>
                    <div className="text-xs text-white/70">3-Day Streak</div>
                  </div>
                )}
                {stats.totalSessions >= 10 && (
                  <div className="text-center">
                    <div className="text-2xl mb-1">💎</div>
                    <div className="text-xs text-white/70">10 Sessions</div>
                  </div>
                )}
                {stats.totalMinutes >= 300 && (
                  <div className="text-center">
                    <div className="text-2xl mb-1">🌟</div>
                    <div className="text-xs text-white/70">5 Hours Total</div>
                  </div>
                )}
                {stats.currentStreak >= 7 && (
                  <div className="text-center">
                    <div className="text-2xl mb-1">🏅</div>
                    <div className="text-xs text-white/70">Week Streak</div>
                  </div>
                )}
                {stats.totalSessions >= 25 && (
                  <div className="text-center">
                    <div className="text-2xl mb-1">👑</div>
                    <div className="text-xs text-white/70">25 Sessions</div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Recent Sessions */}
          <div>
            <h3 className="text-white font-semibold mb-3">Recent Sessions</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recentSessions.length === 0 ? (
                <div className="text-center text-white/50 py-8">
                  <div className="text-4xl mb-2">🎵</div>
                  <div>No sessions yet. Start your first session!</div>
                </div>
              ) : (
                recentSessions.map(session => (
                  <div 
                    key={session.id}
                    className="bg-white/5 rounded-lg p-3 border border-white/10 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-white font-medium">{session.preset}</div>
                        <div className="text-xs text-white/50 flex items-center gap-3 mt-1">
                          <span>{formatDate(session.date)}</span>
                          <span>•</span>
                          <span>{formatDuration(session.duration)}</span>
                          <span>•</span>
                          <span>{session.beatType}</span>
                          {session.completed && (
                            <>
                              <span>•</span>
                              <span className="text-green-400">✓ Completed</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-white/40 text-sm">
                        {session.baseFrequency} Hz
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* Motivational Message */}
          {stats.totalSessions > 0 && (
            <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-lg p-4 border border-green-500/30 text-center">
              <div className="text-white font-medium">
                {stats.currentStreak >= 7 ? (
                  "🌟 Amazing dedication! Keep up the great work!"
                ) : stats.currentStreak >= 3 ? (
                  "🔥 You're on fire! Keep the momentum going!"
                ) : stats.totalMinutes >= 60 ? (
                  "💪 Over an hour of focus time! Impressive!"
                ) : (
                  "🎯 Great start! Keep practicing for better results!"
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionStats;
